import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Switch, Typography, Table, Input, Button, Modal, message, Space, Popconfirm,
  AutoComplete, Tag,
} from 'antd';
import { PlusOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { Title, Text } = Typography;

export default function CommunityFinance() {
  const [settings, setSettings] = useState({ restricted_mode: true, updated_at: null });
  const [list, setList] = useState({ items: [], total: 0, page: 1, limit: 20 });
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [addModal, setAddModal] = useState({ open: false, teamId: null, teamName: '', note: '' });
  const [teamOptions, setTeamOptions] = useState([]);

  const loadSettings = useCallback(async () => {
    try {
      const r = await api.get('/admin/community-finance/settings');
      setSettings(r.data);
    } catch (e) {
      message.error(e.response?.data?.message || 'Falhou carregar config');
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/community-finance/allowed-teams', {
        params: { page: list.page, limit: list.limit, search },
      });
      setList(prev => ({ ...prev, ...r.data }));
    } catch (e) {
      message.error(e.response?.data?.message || 'Falhou carregar lista');
    } finally { setLoading(false); }
  }, [list.page, list.limit, search]);

  useEffect(() => { loadSettings(); }, [loadSettings]);
  useEffect(() => { loadList(); }, [loadList]);

  const toggleRestricted = async (val) => {
    Modal.confirm({
      title: val ? 'Ativar modo restrito?' : 'Desativar modo restrito?',
      content: val
        ? 'Apenas times na whitelist poderao usar o Financeiro. Ok pra beta.'
        : 'Vai liberar Financeiro pra TODOS os times comunidade. Certeza?',
      okText: 'Sim, confirmar',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await api.put('/admin/community-finance/settings', { restricted_mode: val });
          setSettings(prev => ({ ...prev, restricted_mode: val, updated_at: new Date().toISOString() }));
          message.success('Configuracao atualizada');
        } catch (e) {
          message.error(e.response?.data?.message || 'Falhou salvar');
        }
      },
    });
  };

  const searchTeams = async (q) => {
    if (!q || q.length < 2) { setTeamOptions([]); return; }
    try {
      const r = await api.get('/admin/community-finance/team-search', { params: { q } });
      setTeamOptions((r.data.items || []).map(t => ({
        value: String(t.id),
        label: `${t.name}${t.owner_name ? ' — ' + t.owner_name : ''}`,
        team: t,
      })));
    } catch { /* silencioso */ }
  };

  const addTeam = async () => {
    if (!addModal.teamId) return message.warning('Escolha o time');
    try {
      await api.post('/admin/community-finance/allowed-teams', {
        community_team_id: addModal.teamId,
        note: addModal.note?.trim() || null,
      });
      message.success('Time liberado');
      setAddModal({ open: false, teamId: null, teamName: '', note: '' });
      loadList();
    } catch (e) {
      message.error(e.response?.data?.message || 'Falhou adicionar');
    }
  };

  const removeTeam = async (teamId) => {
    try {
      await api.delete(`/admin/community-finance/allowed-teams/${teamId}`);
      message.success('Time removido da whitelist');
      loadList();
    } catch (e) {
      message.error(e.response?.data?.message || 'Falhou remover');
    }
  };

  const columns = [
    { title: 'Time', dataIndex: 'team_name', key: 'team_name',
      render: (v, r) => <><strong>{v}</strong> <Text type="secondary" style={{ fontSize: 11 }}>#{r.community_team_id}</Text></> },
    { title: 'Dono', dataIndex: 'owner_name', key: 'owner_name', render: v => v || '—' },
    { title: 'Liberado em', dataIndex: 'enabled_at', key: 'enabled_at',
      render: v => v ? new Date(v).toLocaleString('pt-BR') : '—' },
    { title: 'Por', dataIndex: 'enabled_by_name', key: 'enabled_by_name', render: v => v || '—' },
    { title: 'Nota', dataIndex: 'note', key: 'note',
      render: v => v ? <Text style={{ fontSize: 12 }}>{v}</Text> : <Text type="secondary" style={{ fontSize: 11 }}>—</Text> },
    { title: '', key: 'action', width: 60,
      render: (_, r) => (
        <Popconfirm title="Remover time da whitelist?" onConfirm={() => removeTeam(r.community_team_id)}>
          <Button danger size="small" icon={<DeleteOutlined />} />
        </Popconfirm>
      ) },
  ];

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <Title level={3}>Cobrança Comunidade</Title>
      <Text type="secondary">Feature gate do módulo Financeiro Comunidade. Só o que você libera aqui aparece pros donos de time.</Text>

      <Card style={{ marginTop: 16 }}>
        <Space align="center" size="large">
          <div>
            <Text strong>Modo restrito</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {settings.restricted_mode
                ? 'Ativado — só times na whitelist abaixo veem o módulo.'
                : 'Desativado — TODOS os times comunidade podem usar.'}
            </Text>
          </div>
          <Switch checked={settings.restricted_mode} onChange={toggleRestricted} />
          {settings.updated_at && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              Última alteração: {new Date(settings.updated_at).toLocaleString('pt-BR')}
            </Text>
          )}
        </Space>
      </Card>

      {settings.restricted_mode && (
        <Card style={{ marginTop: 16 }}
          title={<span>Times liberados <Tag color="green">{list.total}</Tag></span>}
          extra={
            <Space>
              <Input.Search placeholder="Filtrar por nome do time"
                allowClear onSearch={v => { setSearch(v); setList(prev => ({ ...prev, page: 1 })); }} style={{ width: 240 }} />
              <Button icon={<ReloadOutlined />} onClick={loadList} />
              <Button type="primary" icon={<PlusOutlined />}
                onClick={() => setAddModal({ open: true, teamId: null, teamName: '', note: '' })}>
                Adicionar time
              </Button>
            </Space>
          }>
          <Table rowKey="community_team_id" size="middle"
            loading={loading}
            columns={columns}
            dataSource={list.items}
            pagination={{
              total: list.total, current: list.page, pageSize: list.limit,
              onChange: (page) => setList(prev => ({ ...prev, page })),
            }} />
        </Card>
      )}

      <Modal
        title="Adicionar time à whitelist"
        open={addModal.open}
        onCancel={() => setAddModal({ open: false, teamId: null, teamName: '', note: '' })}
        onOk={addTeam}
        okText="Liberar time"
        destroyOnClose
      >
        <div style={{ marginBottom: 12 }}>
          <Text>Time (busca por nome):</Text>
          <AutoComplete
            style={{ width: '100%', marginTop: 4 }}
            options={teamOptions}
            onSearch={searchTeams}
            onSelect={(value, option) => setAddModal(prev => ({
              ...prev, teamId: parseInt(value, 10), teamName: option.team.name,
            }))}
            placeholder="Digite pelo menos 2 letras..."
          />
          {addModal.teamId && (
            <Text type="secondary" style={{ fontSize: 12 }}>Selecionado: {addModal.teamName} (id {addModal.teamId})</Text>
          )}
        </div>
        <div>
          <Text>Nota (opcional):</Text>
          <Input.TextArea
            value={addModal.note}
            onChange={e => setAddModal(prev => ({ ...prev, note: e.target.value }))}
            placeholder="Por que liberou (ex: cliente piloto, pedido do dono)"
            rows={2} maxLength={500}
          />
        </div>
      </Modal>
    </div>
  );
}
